import React from 'react'
import { observer } from 'mobx-react'
import { pseudoRandom } from 'ytil'
import { isPendingMessage, MessageListItem } from '~/models'
import { ChatBackend } from '~/stores/chat'
import { Avatar } from '~/ui/app/media'
import { HBox, Label, LinkLabel } from '~/ui/components'
import { layout, useStyling } from '~/ui/styling'
import { useChat } from './ChatContext'

export interface Props {
  message: MessageListItem
  backend: ChatBackend
}

const ChatMessageSender = observer((props: Props) => {

  const {message, backend} = props

  const {service} = useChat()

  const sender = React.useMemo(() => {
    if (isPendingMessage(message)) {
      return service.sender
    } else {
      return backend.senders.get(message.from)
    }
  }, [backend.senders, message, service.sender])

  const name = React.useMemo(() => {
    if (sender == null) { return '\u202F' }
    return [sender.firstName, sender.lastName].filter(Boolean).join(' ')
  }, [sender])

  const href = React.useMemo(() => {
    if (sender?.type !== 'participant') { return null }
    return `/participants/-/${sender.id}`
  }, [sender])

  const {colors}   = useStyling()
  const colorIndex = pseudoRandom(sender?.id ?? 0, 0, colors.palettes.default.length)

  //------
  // Rendering

  function render() {
    return (
      <HBox gap={layout.padding.inline.s}>
        {sender?.photoURL != null && (
          <Avatar
            {...sender}
            source={sender.photoURL}
            size={avatarSize}
          />
        )}
        {renderName()}
      </HBox>
    )
  }

  function renderName() {
    if (href == null) {
      return (
        <Label tiny bold color={colors.palette('default', colorIndex)}>
          {name}
        </Label>
      )
    }

    return (
      <LinkLabel tiny bold color={colors.palette('default', colorIndex)} href={href}>
        {name}
      </LinkLabel>
    )
  }


  return render()

})

export default ChatMessageSender

export const avatarSize = {
  width:  12,
  height: 12,
}